<template>
  <div
    v-if="open"
    class="dlg-overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="ticket-view-title"
    @keydown.esc="emitClose"
  >
    <div class="dlg">
      <div class="dlg-header">
        <h3 id="ticket-view-title">
          Ticket #{{ ticket?.id }} — {{ ticket?.subject || '—' }}
          <span
            v-if="otherFullName"
            class="muted"
          > — {{ otherFullName }}</span>
          <span
            v-if="ticket?.isPrivate || ticket?.is_private"
            class="badge"
            style="margin-left:6px;"
          >privat</span>
        </h3>

        <div class="header-actions">
          <template v-if="isAdmin && isTicketmaster">
            <label
              class="muted"
              for="status-sel"
            >Status:</label>
            <select
              id="status-sel"
              v-model="status"
              :disabled="saving"
              @change="changeStatus"
            >
              <option value="open">
                Offen
              </option>
              <option value="in_progress">
                In Bearbeitung
              </option>
              <option value="resolved">
                Gelöst
              </option>
              <option value="closed">
                Geschlossen
              </option>
            </select>
          </template>
          <button
            class="icon-btn"
            aria-label="Schließen"
            @click="emitClose"
          >
            ✕
          </button>
        </div>
      </div>

      <div
        ref="threadEl"
        class="thread"
        aria-live="polite"
      >
        <div
          v-for="m in (ticket?.messages || [])"
          :key="m.id"
          :class="['msg', me(m) ? 'me' : 'other']"
        >
          <div class="bubble">
            <div class="meta">
              <span
                v-if="me(m)"
                class="author"
              >Ich</span>
              <span
                v-else-if="senderName(m)"
                class="author"
              >{{ senderName(m) }}</span>
              <span class="time">{{ time(m.createdAt || m.created_at) }}</span>
            </div>
            <p
              v-if="m.message"
              class="text"
            >
              {{ m.message }}
            </p>

            <div
              v-if="(m.attachments || []).length"
              class="thumb-strip"
              aria-label="Anhänge"
            >
              <div
                v-for="a in m.attachments"
                :key="a.id"
                class="thumb"
                role="button"
                tabindex="0"
                :title="`Öffnen: ${a.filename || 'Datei'}`"
                @click="openAttachment(a, m.attachments)"
                @keydown.enter="openAttachment(a, m.attachments)"
              >
                <img
                  v-if="isImageLike(a) && getPreviewUrl(a)"
                  :src="getPreviewUrl(a)"
                  :alt="a.filename || 'Bild'"
                  loading="lazy"
                >
                <div
                  v-else
                  class="thumb-doc"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                    aria-hidden="true"
                  >
                    <path
                      fill="currentColor"
                      d="M6 2h7l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm7 1.5V8h4.5L13 3.5zM7.75 18.5h8.5c.69 0 1.25-.56 1.25-1.25v-5.5c0-.69-.56-1.25-1.25-1.25h-8.5c-.69 0-1.25.56-1.25 1.25v5.5c0 .69.56 1.25 1.25 1.25z"
                    />
                  </svg>
                  <span class="name">{{ a.filename || 'Anhang' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p
          v-if="!ticket || (ticket.messages || []).length === 0"
          class="muted empty"
        >
          Noch keine Nachrichten.
        </p>
      </div>

      <!-- Composer unten -->
      <form
        v-if="!isClosed"
        class="composer"
        @submit.prevent="send"
      >
        <label
          class="visually-hidden"
          for="chat-input"
        >Nachricht</label>
        <input
          id="chat-input"
          v-model.trim="text"
          class="input"
          type="text"
          :placeholder="composerDisabled ? 'Ticket geschlossen – keine Antwort möglich' : 'Nachricht schreiben…'"
          :disabled="composerDisabled"
          autocomplete="off"
        >
        <input
          id="file-input"
          ref="fileInput"
          type="file"
          class="visually-hidden"
          multiple
          @change="onFiles"
        >
        <button
          type="button"
          class="btn"
          :disabled="composerDisabled"
          title="Anhänge"
          @click="triggerFile"
        >
          📎
        </button>
        <button
          type="submit"
          class="btn btn-primary"
          :disabled="composerDisabled || !text"
        >
          <span
            v-if="saving"
            class="spinner"
            aria-hidden="true"
          />
          Senden
        </button>
      </form>
      <p
        v-if="isCreator && isClosed"
        class="help muted"
        style="margin-top:6px;"
      >
        Ticket geschlossen – als Ersteller können Sie hier nicht mehr antworten.
      </p>

      <!-- Ausgewählte Dateien Vorschau (kleine Chips) -->
      <div
        v-if="files.length"
        class="file-chips"
      >
        <button
          v-for="(f, i) in files"
          :key="i"
          class="chip"
          type="button"
          @click="removeFile(i)"
        >
          {{ f.name }}
          <span aria-hidden="true">×</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Overlay-Viewer -->
  <div
    v-if="viewer.open"
    class="viewer-overlay"
    role="dialog"
    aria-modal="true"
    tabindex="-1"
    @keydown.esc="closeViewer"
  >
    <div class="viewer">
      <div class="viewer-header">
        <span class="viewer-title">{{ currentFileName }}</span>
        <div class="viewer-actions">
          <button
            class="btn"
            :disabled="viewerLoading || !canPrint"
            title="Drucken"
            @click="printCurrent"
          >
            Drucken
          </button>
          <button
            class="btn btn-primary"
            :disabled="viewerLoading"
            title="Download"
            @click="downloadCurrent"
          >
            Download
          </button>
          <button
            class="btn"
            aria-label="Schließen"
            title="Schließen"
            @click="closeViewer"
          >
            Schließen
          </button>
        </div>
      </div>
      <div class="viewer-body">
        <div
          v-if="viewerLoading"
          class="viewer-loading"
        >
          Lädt …
        </div>
        <template v-else>
          <img
            v-if="isCurrentImage && currentDataUrl"
            :src="currentDataUrl"
            alt=""
            class="viewer-media"
          >
          <iframe
            v-else-if="isCurrentPdf && currentObjectUrl"
            :src="currentObjectUrl"
            title="Dokument"
            class="viewer-frame"
          />
          <div
            v-else
            class="viewer-fallback"
          >
            <p class="muted">
              Vorschau nicht verfügbar. Sie können die Datei herunterladen.
            </p>
          </div>
        </template>

        <button
          class="nav prev"
          :disabled="viewerList.length <= 1"
          aria-label="Vorheriger"
          @click="prevInViewer"
        >
          ‹
        </button>
        <button
          class="nav next"
          :disabled="viewerList.length <= 1"
          aria-label="Nächster"
          @click="nextInViewer"
        >
          ›
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, ref, watch, onMounted, nextTick} from 'vue';
import {useTicketsStore} from '@/stores/tickets';
import {getTicketAttachmentBase64} from '@/api/tickets';
import {useAuthStore} from '@/stores/auth';

const props = defineProps({
  open: {type: Boolean, default: false},
  ticketId: {type: [Number, String], required: true}
});
const emit = defineEmits(['close']);

const auth = useAuthStore();
const roleId = computed(() => auth.user?.roleId ?? auth.user?.role_id ?? null);
const isAdmin = computed(() => roleId.value === 1);


const store = useTicketsStore();
const ticket = computed(() => (store.current && store.current.id === Number(props.ticketId)) ? store.current : null);

const status = ref('open');
const saving = ref(false);

const text = ref('');
const files = ref([]);
const fileInput = ref(null);

const currentUserId = computed(() => auth.user?.id ?? auth.user?.userId ?? auth.user?.user_id ?? null);
// Ticketmaster aktuell: user_id = 2
const isTicketmaster = computed(() => Number(currentUserId.value) === 2);
const isCreator = computed(() => {
  const t = ticket.value;
  const creator = t?.creatorUserId ?? t?.creator_user_id ?? null;

  return creator != null && currentUserId.value != null && Number(creator) === Number(currentUserId.value);
});
const isClosed = computed(() => (ticket.value?.status || status.value) === 'closed');
const composerDisabled = computed(() => saving.value || (isCreator.value && isClosed.value));

const threadEl = ref(null);

function emitClose() {
  emit('close');
}

onMounted(async () => {
  if (props.open) {
    await ensureLoaded();
    if (ticket.value?.id) {
      await store.markRead(ticket.value.id);
    }
  }
});

watch(() => props.open, async (v) => {
  if (v) {
    await ensureLoaded();
    if (ticket.value?.id) {
      await store.markRead(ticket.value.id);
    }
  }
});

watch(ticket, async (t) => {
  if (!t) return;
  status.value = t.status || 'open';
  await prefetchPreviews();
  await nextTick();
  scrollToBottom();
}, {deep: true});

async function ensureLoaded() {
  try {
    await store.fetchTicket(Number(props.ticketId));
    status.value = ticket.value?.status || 'open';
    await prefetchPreviews();
    await nextTick();
    scrollToBottom();
  } catch (e) {
    void 0;
  }
}

function me(m) {
  // Primär Auth-Store verwenden (robust für Ticketmaster/Admin)
  const uid = Number(currentUserId.value ?? 0);
  const mid = Number((m?.userId ?? m?.user_id ?? 0));
  if (uid && mid) {
    return uid === mid;
  }
  // Fallback (Legacy): localStorage
  const lsId = Number(JSON.parse(localStorage.getItem('user') || '{}')?.id ?? 0);
  return lsId && (mid ? lsId === mid : (m?.userId === lsId || m?.user_id === lsId));
}

function senderName(m) {
  return m.userName || m.user_name || '';
}

// Vollständiger Name (Vor- und Zuname) des Gegenübers für den Header
const otherFullName = computed(() => {
  const t = ticket.value;
  if (!t || !Array.isArray(t.messages)) return '';
  const otherMsg = t.messages.find((mm) => !me(mm));
  if (!otherMsg) return '';
  const first = otherMsg.userFirstName || otherMsg.user_first_name || '';
  const last = otherMsg.userLastName || otherMsg.user_last_name || '';
  const full = [first, last].filter(Boolean).join(' ').trim();
  return full || senderName(otherMsg) || '';
});

function time(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(d);
}

function isImageLike(a) {
  const mime = (a?.mime_type || a?.mimeType || '').toLowerCase();
  const name = (a?.filename || '').toLowerCase();
  return mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name);
}

// Persistenter Preview-Cache (überlebt Ersetzungen von ticket/messages)
const previewCache = ref(new Map());

function getPreviewUrl(a) {
  if (!a) return null;
  if (a.previewUrl) return a.previewUrl;

  const key = a.id != null ? String(a.id) : null;
  if (key && previewCache.value.has(key)) {
    return previewCache.value.get(key);
  }

  if (a._base64) {
    const mtRaw = a.mime || a.mime_type || a.mimeType || '';
    const mt = String(mtRaw).startsWith('image/') ? String(mtRaw) : 'image/*';
    return `data:${mt};base64,${a._base64}`;
  }
  return null;
}

async function prefetchPreviews() {
  const t = ticket.value;
  if (!t || !Array.isArray(t.messages)) return;

  for (const m of t.messages) {
    const atts = Array.isArray(m.attachments) ? m.attachments : [];
    for (const a of atts) {
      // Basisinfos wie im NotificationViewDialog bereitstellen
      a.name = a.name || a.filename || 'Anhang';
      a.mime = a.mime || a.mime_type || null;
      a.base64Url = a.base64Url || `/api/tickets/${encodeURIComponent(t.id)}/attachments/${encodeURIComponent(a.id)}/base64`;

      // Bereits vorhandene Preview in Cache spiegeln
      if (a.previewUrl && a.id != null) {
        try {
          previewCache.value.set(String(a.id), a.previewUrl);
        } catch (e) { /* ignore */
        }
      }

      // Nur für Bild-Anhänge Preview laden
      if (!isImageLike(a) || a.previewUrl) continue;
      try {
        await ensureBase64(a, t.id);
        if (a._base64) {
          const mt = a.mime && String(a.mime).startsWith('image/') ? a.mime : 'image/*';
          a.previewUrl = `data:${mt};base64,${a._base64}`;
          if (a.id != null) {
            try {
              previewCache.value.set(String(a.id), a.previewUrl);
            } catch (e) { /* ignore */
            }
          }
        }
      } catch (e) {
        void 0;
      }
    }
  }
}

function scrollToBottom() {
  const el = threadEl.value;
  if (!el) return;
  try {
    el.scrollTop = el.scrollHeight;
  } catch (e) {
    void 0;
  }
}

/* Composer */
function onFiles(e) {
  files.value = Array.from(e.target.files || []);
}

function removeFile(i) {
  files.value.splice(i, 1);
  if (files.value.length === 0 && fileInput.value) {
    fileInput.value.value = '';
  }
}

function triggerFile() {
  try {
    fileInput.value?.click();
  } catch (e) {
    void 0;
  }
}

async function send() {
  if (!ticket.value) return;
  saving.value = true;
  try {
    await store.reply(ticket.value.id, {message: text.value, attachments: files.value});
    text.value = '';
    files.value = [];
    if (fileInput.value) fileInput.value.value = '';

    // Nach der Aktualisierung Previews erneut laden (ersetzt verlorene previewUrls)
    await prefetchPreviews();

    await nextTick();
    scrollToBottom();
  } catch {
    // still
  } finally {
    saving.value = false;
  }
}

/* Status nur für Admin änderbar */
async function changeStatus() {
  if (!isAdmin.value || !ticket.value) return;
  const s = status.value;
  saving.value = true;
  try {
    await store.setStatus(ticket.value.id, s);
  } catch {
    // still
  } finally {
    saving.value = false;
  }
}

/* Viewer-Logik */
const viewer = ref({open: false, list: [], index: 0});
const viewerLoading = ref(false);
const currentObjectUrl = ref(null);

const viewerList = computed(() => Array.isArray(viewer.value.list) ? viewer.value.list : []);
const currentAttachment = computed(() => viewerList.value[viewer.value.index] || null);
const currentFileName = computed(() => currentAttachment.value?.filename || currentAttachment.value?.name || 'Datei');
const isCurrentImage = computed(() => {
  const m = (currentAttachment.value?.mime_type || '').toLowerCase();
  if (m) return m.startsWith('image/');
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test((currentAttachment.value?.filename || '').toLowerCase());
});
const isCurrentPdf = computed(() => {
  const m = (currentAttachment.value?.mime_type || '').toLowerCase();
  return m === 'application/pdf' || /\.pdf$/.test((currentAttachment.value?.filename || '').toLowerCase());
});
const currentDataUrl = computed(() => {
  const a = currentAttachment.value;
  if (!a || !a._base64) return null;
  const mime = a.mime_type || 'application/octet-stream';
  return `data:${mime};base64,${a._base64}`;
});
const canPrint = computed(() => isCurrentImage.value || isCurrentPdf.value);

async function openAttachment(att, list) {
  if (!ticket.value) return;
  const arr = Array.isArray(list) ? list : (att ? [att] : []);
  const idx = arr.findIndex(x => String(x?.id) === String(att?.id));
  viewer.value.list = arr;
  viewer.value.index = idx >= 0 ? idx : 0;
  viewer.value.open = true;
  await loadCurrent();
}

function closeViewer() {
  viewer.value.open = false;
  if (currentObjectUrl.value) {
    try {
      URL.revokeObjectURL(currentObjectUrl.value);
    } catch (e) {
      void 0;
    }
    currentObjectUrl.value = null;
  }
}

async function loadCurrent() {
  const a = currentAttachment.value;
  if (!a) return;
  // Vorherige ObjectURL freigeben
  if (currentObjectUrl.value) {
    try {
      URL.revokeObjectURL(currentObjectUrl.value);
    } catch (e) {
      void 0;
    }
    currentObjectUrl.value = null;
  }
  viewerLoading.value = true;
  try {
    await ensureBase64(a, ticket.value?.id);
    if (isCurrentPdf.value && a._base64) {
      const blob = base64ToBlob(a._base64, a.mime || a.mime_type || 'application/pdf');
      currentObjectUrl.value = URL.createObjectURL(blob);
    }
  } catch {
    // still
  } finally {
    viewerLoading.value = false;
  }
}

async function nextInViewer() {
  if (viewerList.value.length <= 1) return;
  viewer.value.index = (viewer.value.index + 1) % viewerList.value.length;
  await loadCurrent();
}

async function prevInViewer() {
  if (viewerList.value.length <= 1) return;
  const len = viewerList.value.length;
  viewer.value.index = (viewer.value.index - 1 + len) % len;
  await loadCurrent();
}

function base64ToBlob(b64, mime) {
  const byteString = atob(b64);
  const len = byteString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = byteString.charCodeAt(i);
  return new Blob([bytes], {type: mime || 'application/octet-stream'});
}

/**
 * Lädt Base64-Content eines Attachments (falls noch nicht vorhanden)
 * und normalisiert mime/name-Felder (wie im NotificationViewDialog).
 */
async function ensureBase64(att, ticketId) {
  if (!att || att._base64) return;
  const id = ticketId ?? (ticket.value?.id ?? null);
  if (!id) throw new Error('ticket id missing');
  const data = await getTicketAttachmentBase64(id, att.id);
  const b64 = data?.base64 || null;
  const mime = data?.mimeType || att.mime || att.mime_type || 'application/octet-stream';
  const name = data?.filename || att.name || att.filename || 'Datei';

  if (!b64) throw new Error('content empty');
  att._base64 = b64;
  att.mime = mime;
  att.mime_type = mime;
  att.name = name;
  att.filename = name;
}

async function downloadCurrent() {
  const a = currentAttachment.value;
  if (!a) return;
  try {
    await ensureBase64(a, ticket.value.id);
    if (!a._base64) throw new Error('content empty');
    const blob = base64ToBlob(a._base64, a.mime_type || a.mime || 'application/octet-stream');
    const url = URL.createObjectURL(blob);
    const el = document.createElement('a');
    el.href = url;
    el.download = a.filename || a.name || 'download';
    document.body.appendChild(el);
    el.click();
    el.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    // still
  }
}

async function printCurrent() {
  const a = currentAttachment.value;
  if (!a || !canPrint.value) return;
  try {
    if (!a._base64) {
      const data = await getTicketAttachmentBase64(ticket.value.id, a.id);
      a._base64 = data?.base64 || null;
      a.mime_type = data?.mimeType || a.mime_type || null;
    }
    if (isCurrentImage.value && a._base64) {
      const dataUrl = `data:${a.mime_type || 'image/*'};base64,${a._base64}`;
      const iframe = document.createElement('iframe');
      Object.assign(iframe.style, {position: 'fixed', right: 0, bottom: 0, width: 0, height: 0, border: 0});
      document.body.appendChild(iframe);
      const html = `<!doctype html><html><head><title>${(a.filename || 'Bild').replace(/</g, '&lt;')}</title><style>html,body{margin:0}img{max-width:100vw;max-height:100vh;}</style></head><body><img src="${dataUrl}" alt=""></body></html>`;
      iframe.srcdoc = html;
      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (e) {
          void 0;
        }
        setTimeout(() => iframe.remove(), 2000);
      };
    } else if (isCurrentPdf.value) {
      const blob = base64ToBlob(a._base64, 'application/pdf');
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      Object.assign(iframe.style, {position: 'fixed', right: 0, bottom: 0, width: 0, height: 0, border: 0});
      document.body.appendChild(iframe);
      iframe.src = url;
      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (e) {
          void 0;
        }
        setTimeout(() => {
          try {
            URL.revokeObjectURL(url);
          } catch (e) {
            void 0;
          }
          iframe.remove();
        }, 5 * 60 * 1000);
      };
    }
  } catch (e) {
    void 0;
  }
}
</script>

<style scoped>
.dlg-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dlg {
  width: min(920px, 96vw);
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, .18);
  padding: 16px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}

.dlg-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--border);
  padding-bottom: 8px;
  margin-bottom: 12px;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.thread {
  overflow: auto;
  flex: 1 1 auto;
  padding: 6px 6px 0 6px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 10px;
}

.msg {
  display: flex;
  gap: 8px;
  margin: 10px 6px;
  align-items: flex-end;
  width: auto;
}

.msg.me {
  justify-content: flex-end;
}

.bubble {
  max-width: 68%;
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 8px 10px;
  background: var(--surface);
  box-shadow: 0 1px 2px rgba(0, 0, 0, .04);
}

.msg.me .bubble {
  background: color-mix(in srgb, var(--primary) 8%, white);
  border-color: color-mix(in srgb, var(--primary) 40%, var(--border));
}

.meta {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 4px;
}

.text {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.thumb-strip {
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.thumb {
  flex: 0 0 auto;
  width: 120px;
  height: 84px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.thumb img {
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
  border-radius: 6px;
}

.thumb-doc {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: var(--muted);
  padding: 6px;
  text-align: center;
}

.thumb-doc .name {
  font-size: 12px;
  line-height: 1.2;
  word-break: break-all;
  color: var(--text);
}

.composer {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 10px;
}

.input {
  flex: 1 1 auto;
  height: 38px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
}

.file-chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 6px;
}

.chip {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  border-radius: 999px;
  padding: 3px 8px;
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  width: 36px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
}

.icon-btn:hover {
  background: color-mix(in srgb, var(--primary) 8%, transparent);
}

.viewer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}

.viewer {
  width: min(100%, 960px);
  max-height: 92vh;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, .15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
}

.viewer-title {
  font-size: 14px;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.viewer-actions {
  display: inline-flex;
  gap: 8px;
}

.viewer-body {
  position: relative;
  background: var(--bg);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 360px;
  height: 70vh;
  padding: 12px;
}

.viewer-loading {
  color: var(--muted);
  font-size: 14px;
}

.viewer-media {
  max-width: 100%;
  max-height: 100%;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .06);
}

.viewer-frame {
  width: 100%;
  height: 100%;
  border: 0;
  background: var(--surface);
  border-radius: 6px;
}

.viewer-fallback {
  color: var(--muted);
  font-size: 14px;
  text-align: center;
}

/* Galerie-Navigation */
.nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 44px;
  width: 44px;
  border-radius: 50%;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.nav:hover {
  background: color-mix(in srgb, var(--primary) 8%, transparent);
}

.nav:disabled {
  opacity: .6;
  cursor: default;
}

.nav.prev {
  left: 12px;
}

.nav.next {
  right: 12px;
}

.visually-hidden {
  position: absolute !important;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.empty {
  text-align: center;
  margin: 20px 0;
}

/* Badge (Style-Guide-konform, neutral/muted) */
.badge {
  display: inline-block;
  font-size: 12px;
  line-height: 16px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
  color: var(--muted);
  background: var(--surface);
}
</style>
